/**
 * Search query syntax (FR-SRC-03): the help shown in the search box and a light client
 * check so obvious mistakes are explained before the query is sent. The server (D4) is
 * the real parser; this only catches what a person would want told straight away.
 */
export const SYNTAX: { example: string; meaning: string }[] = [
  { example: 'cañete', meaning: 'Any word form; accents and small typos are forgiven' },
  { example: '"deed of sale"', meaning: 'The exact phrase' },
  { example: 'lease AND makati', meaning: 'Both words (AND is the default)' },
  { example: 'invoice OR receipt', meaning: 'Either word' },
  { example: 'contract NOT draft', meaning: 'Leave out documents with a word (or -draft)' },
  { example: 'perm* or 20?6', meaning: 'Wildcards: * many letters, ? one letter' },
  { example: 'type:invoice tag:urgent', meaning: 'Field search: type, tag, correspondent, cabinet, folder, status' },
  { example: 'from:2026-01-01 to:2026-03-31', meaning: 'Document date range (or date:2025 for a whole year)' },
  { example: 'created:last-month', meaning: 'Relative dates: today, last-week, last-month, this-year' },
  { example: 'amount>50000', meaning: 'Amounts and numbers: > < >= <=' },
  { example: 'asn:000123', meaning: 'The archive serial number on the paper original' },
];

export const FIELDS = ['type', 'tag', 'correspondent', 'cabinet', 'folder', 'status', 'from', 'to', 'date', 'created', 'added', 'amount', 'asn', 'title', 'content'] as const;

/** Returns a plain-language problem with the query, or null when it looks fine. */
export function checkQuery(q: string): string | null {
  const text = q.trim();
  if (!text) return null;
  const quotes = (text.match(/"/g) ?? []).length;
  if (quotes % 2) return 'A quotation mark is not closed. Add the closing " after the phrase.';
  let depth = 0;
  for (const ch of text.replace(/"[^"]*"/g, '')) {
    if (ch === '(') depth++;
    if (ch === ')') depth--;
    if (depth < 0) return 'There is a closing bracket ) without an opening one.';
  }
  if (depth > 0) return 'A bracket ( is not closed.';
  const unquoted = text.replace(/"[^"]*"/g, ' ');
  if (/^(AND|OR)\b|\b(AND|OR|NOT)$/.test(unquoted.trim())) return 'AND, OR and NOT need a word on each side, e.g. invoice OR receipt.';
  for (const m of unquoted.matchAll(/(^|\s)([a-z]+):(\S*)/gi)) {
    const field = m[2].toLowerCase();
    if (!(FIELDS as readonly string[]).includes(field)) return `“${m[2]}:” is not a search field. Try ${FIELDS.slice(0, 5).join(':, ')}:.`;
    if (!m[3]) return `Add a value after “${m[2]}:”, e.g. ${field}:invoice.`;
  }
  return null;
}
